import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ForceInviteConfigSection from "./mym/ForceInviteConfigSection";
import ForceAutoOffSection from "./mym/ForceAutoOffSection";
import { MYM_GAME_LABELS, MYM_TITLE_CATEGORIES } from "./mym/useMymTitlesConfig";
import Header from "./Header";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const AdminConfig = () => {
  const [loginRequired, setLoginRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mymConfig, setMymConfig] = useState([]);
  const [mymLoading, setMymLoading] = useState(true);
  const [mymSaving, setMymSaving] = useState(false);
  const [splashEditOpen, setSplashEditOpen] = useState(false);
  const [splashEditItem, setSplashEditItem] = useState(null);
  const [splashTitle, setSplashTitle] = useState("");
  const [splashDescription, setSplashDescription] = useState("");
  const [forceWarningOpen, setForceWarningOpen] = useState(false);
  const [forceWarningMessage, setForceWarningMessage] = useState("");
  const [forceExclusiveOpen, setForceExclusiveOpen] = useState(false);
  const [pendingForceId, setPendingForceId] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/login_required`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setLoginRequired(data.setting_value === "true");
        } else {
          setLoginRequired(false);
        }
      } catch (err) {
        console.error("Failed to fetch login_required:", err);
        setLoginRequired(false);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchMymConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
        const data = await res.json();
        if (data.config && Array.isArray(data.config)) {
          const knownIds = Object.keys(MYM_GAME_LABELS);
          const normalized = knownIds.map((id) => {
            const existing = data.config.find((item) => item.id === id);
            return (
              existing || {
                id,
                hidden: false,
                enabled: true,
                splashTitle: "",
                splashDescription: "",
                forceModal: false,
              }
            );
          });
          setMymConfig(normalized);
        }
      } catch (err) {
        console.error("Failed to fetch MYM config:", err);
      } finally {
        setMymLoading(false);
      }
    };
    fetchMymConfig();
    const socket = io(API_BASE, { transports: ["websocket"] });
    socket.on("mym.force_open", fetchMymConfig);
    socket.on("mym.force_clear", fetchMymConfig);
    return () => {
      socket.disconnect();
    };
  }, []);

  const handleLoginRequiredChange = async (event) => {
    const value = event.target.checked;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/login-required`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        },
        body: JSON.stringify({ loginRequired: value }),
      });
      if (res.ok) {
        setLoginRequired(value);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error(data.message || "Failed to update");
      }
    } catch (err) {
      console.error("Failed to update login required:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateMymItem = (id, updates) => {
    setMymConfig((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const saveMymConfig = async (configToSave) => {
    const raw = configToSave ?? mymConfig;
    const payload = raw.map((item) => ({
      ...item,
      splashEnabled: !!item.enabled,
      forceModal: !!item.forceModal,
    }));
    setMymSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-titles-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        },
        body: JSON.stringify({ config: payload }),
      });
      if (res.ok) {
        if (configToSave) setMymConfig(payload);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error(data.message || "Failed to save MYM config");
      }
    } catch (err) {
      console.error("Failed to save MYM config:", err);
    } finally {
      setMymSaving(false);
    }
  };

  const handleMymToggle = (id, field, value) => {
    const currentItem = mymConfig.find((i) => i.id === id);
    if (!currentItem) return;

    // If trying to hide or disable a forced card, block and show warning
    if (currentItem.forceModal && (field === "hidden" || field === "enabled")) {
      setForceWarningMessage(
        "Force modal is ON for this card. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    // When turning forceModal ON, enforce prerequisites and exclusivity
    if (field === "forceModal" && value) {
      // Must be visible and enabled
      if (currentItem.hidden || !currentItem.enabled) {
        setForceWarningMessage(
          "To force this card, both 'Show tile' and 'On Air' must be ON. Please enable them first."
        );
        setForceWarningOpen(true);
        return;
      }

      // Only one forced card at a time
      const alreadyForced = mymConfig.find(
        (item) => item.id !== id && item.forceModal
      );
      if (alreadyForced) {
        setPendingForceId(id);
        setForceExclusiveOpen(true);
        return;
      }
    }

    // Normal update path
    updateMymItem(id, { [field]: value });
    const next = mymConfig.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    saveMymConfig(next);
  };

  const handleGoLiveStop = (id) => {
    const currentItem = mymConfig.find((item) => item.id === id);
    if (!currentItem) return;

    const isLive = !currentItem.hidden && !!currentItem.enabled;
    if (isLive && currentItem.forceModal) {
      setForceWarningMessage(
        "Force modal is ON for this card. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    const next = mymConfig.map((item) =>
      item.id === id
        ? { ...item, hidden: isLive, enabled: !isLive }
        : item
    );
    setMymConfig(next);
    saveMymConfig(next);
  };

  const handleCategoryBulk = (ids, field, turnOn) => {
    const idSet = new Set(ids);
    const targets = mymConfig.filter((item) => idSet.has(item.id));
    if (!targets.length) return;

    if (!turnOn && targets.some((item) => item.forceModal)) {
      setForceWarningMessage(
        "Force modal is ON for a card in this category. You cannot hide it or turn off On Air. Please turn off Force modal first."
      );
      setForceWarningOpen(true);
      return;
    }

    const next = mymConfig.map((item) => {
      if (!idSet.has(item.id)) return item;
      if (field === "hidden") return { ...item, hidden: !turnOn };
      return { ...item, enabled: !!turnOn };
    });
    setMymConfig(next);
    saveMymConfig(next);
  };

  const confirmForceExclusive = () => {
    if (!pendingForceId) {
      setForceExclusiveOpen(false);
      return;
    }
    const next = mymConfig.map((item) => {
      if (item.id === pendingForceId) {
        return { ...item, forceModal: true };
      }
      if (item.forceModal) {
        return { ...item, forceModal: false };
      }
      return item;
    });
    setMymConfig(next);
    saveMymConfig(next);
    setPendingForceId(null);
    setForceExclusiveOpen(false);
  };

  const cancelForceExclusive = () => {
    setPendingForceId(null);
    setForceExclusiveOpen(false);
  };

  const openSplashEdit = (item) => {
    setSplashEditItem(item);
    setSplashTitle(item.splashTitle || "");
    setSplashDescription(item.splashDescription || "");
    setSplashEditOpen(true);
  };

  const closeSplashEdit = () => {
    setSplashEditOpen(false);
    setSplashEditItem(null);
  };

  const handleSplashEditSave = () => {
    if (!splashEditItem) return;
    const next = mymConfig.map((item) =>
      item.id === splashEditItem.id
        ? { ...item, splashTitle, splashDescription }
        : item
    );
    setMymConfig(next);
    saveMymConfig(next);
    closeSplashEdit();
  };

  return (
    <>
      <Header />
      <Container
        maxWidth="md"
        sx={{
          mt: 2,
          mb: 2,
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          Dashboard Home Config
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: "#1E1E1E",
              border: "1px solid #333",
              borderRadius: "12px",
              mb: 3,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={loginRequired}
                  onChange={handleLoginRequiredChange}
                  disabled={saving}
                  color="primary"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#1DB850",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#1DB850",
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 500 }}>
                    Login required — when ON, users must log in to access the site; logout redirects to /login
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#888", mt: 0.5 }}>
                    Ticket pages (/tickets, purchase, success) always stay public — no login needed to buy tickets.
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
          </Paper>
        )}

        {/* MYM Config */}
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "#fff",
            fontSize: { xs: "1.25rem", sm: "1.4rem" },
          }}
        >
          MYM Titles Config
        </Typography>
        {mymLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        ) : (
          MYM_TITLE_CATEGORIES.map((category) => (
            <Box key={category.id} sx={{ mb: 2.5 }}>
              {(() => {
                const items = category.ids
                  .map((id) => mymConfig.find((entry) => entry.id === id))
                  .filter(Boolean);
                const allShown = items.length > 0 && items.every((item) => !item.hidden);
                const allOnAir = items.length > 0 && items.every((item) => !!item.enabled);
                const bulkButtonSx = (active) => ({
                  color: "#fff",
                  fontWeight: 700,
                  backgroundColor: active ? "#1DB850" : "#e53935",
                  "&:hover": {
                    backgroundColor: active ? "#17a348" : "#c62828",
                  },
                });
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#00FFFF",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                      }}
                    >
                      {category.label}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={mymSaving}
                        onClick={() => handleCategoryBulk(category.ids, "hidden", !allShown)}
                        sx={bulkButtonSx(!allShown)}
                      >
                        {allShown ? "Stop show" : "Show all"}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={mymSaving}
                        onClick={() => handleCategoryBulk(category.ids, "enabled", !allOnAir)}
                        sx={bulkButtonSx(!allOnAir)}
                      >
                        {allOnAir ? "Stop on air" : "On Air all"}
                      </Button>
                    </Box>
                  </Box>
                );
              })()}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "#1E1E1E",
                  border: "1px solid #333",
                  borderRadius: "12px",
                }}
              >
                {category.ids.map((id) => {
                  const item = mymConfig.find((entry) => entry.id === id);
                  if (!item) return null;
                  const isLive = !item.hidden && !!item.enabled;
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        py: 1.5,
                        borderBottom: "1px solid #333",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Typography sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                        {MYM_GAME_LABELS[item.id] || item.id}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!item.hidden}
                              onChange={(e) =>
                                handleMymToggle(item.id, "hidden", !e.target.checked)
                              }
                              disabled={mymSaving}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#1DB850" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: "#1DB850",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: "#aaa" }}>
                              Show tile
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!item.enabled}
                              onChange={(e) =>
                                handleMymToggle(item.id, "enabled", e.target.checked)
                              }
                              disabled={mymSaving}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#1DB850" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: "#1DB850",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: "#aaa" }}>
                              On Air (ribbon)
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!item.forceModal}
                              onChange={(e) =>
                                handleMymToggle(
                                  item.id,
                                  "forceModal",
                                  e.target.checked
                                )
                              }
                              disabled={mymSaving}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#ff4081",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: "#ff4081",
                                  },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: "#aaa" }}>
                              Force modal (lock close)
                            </Typography>
                          }
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openSplashEdit(item)}
                          sx={{
                            color: "#00FFFF",
                            borderColor: "#00FFFF",
                            "&:hover": { borderColor: "#00FFFF", bgcolor: "rgba(0,255,255,0.08)" },
                          }}
                        >
                          Edit splash
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={mymSaving}
                          onClick={() => handleGoLiveStop(item.id)}
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            backgroundColor: isLive ? "#e53935" : "#1DB850",
                            "&:hover": {
                              backgroundColor: isLive ? "#c62828" : "#17a348",
                            },
                          }}
                        >
                          {isLive ? "Stop" : "Go Live"}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          ))
        )}

        {/* Edit Splash Modal */}
        <Dialog
          open={splashEditOpen}
          onClose={closeSplashEdit}
          PaperProps={{
            sx: {
              backgroundColor: "#1E1E1E",
              border: "1px solid #333",
              borderRadius: "12px",
              minWidth: 360,
            },
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>
            Edit splash — {splashEditItem ? MYM_GAME_LABELS[splashEditItem.id] : ""}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Splash title"
              value={splashTitle}
              onChange={(e) => setSplashTitle(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
              InputProps={{ sx: { color: "#fff" } }}
              InputLabelProps={{ sx: { color: "#aaa" } }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Splash description"
              value={splashDescription}
              onChange={(e) => setSplashDescription(e.target.value)}
              InputProps={{ sx: { color: "#fff" } }}
              InputLabelProps={{ sx: { color: "#aaa" } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={closeSplashEdit} sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSplashEditSave}
              sx={{
                background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                color: "#fff",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Force constraints warning */}
        <Dialog
          open={forceWarningOpen}
          onClose={() => setForceWarningOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: "#1E1E1E",
              border: "1px solid #333",
              borderRadius: "12px",
              minWidth: 320,
            },
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Force modal rule</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "#ddd" }}>
              {forceWarningMessage}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setForceWarningOpen(false)} sx={{ color: "#fff" }}>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Only one forced card at a time */}
        <Dialog
          open={forceExclusiveOpen}
          onClose={cancelForceExclusive}
          PaperProps={{
            sx: {
              backgroundColor: "#1E1E1E",
              border: "1px solid #333",
              borderRadius: "12px",
              minWidth: 320,
            },
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Switch forced card?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "#ddd" }}>
              Another card is already forced. Do you want to turn off force on that
              card and force this one instead?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={cancelForceExclusive} sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              onClick={confirmForceExclusive}
              sx={{ color: "#fff" }}
              variant="contained"
            >
              Yes, switch
            </Button>
          </DialogActions>
        </Dialog>

        <ForceInviteConfigSection />
        <ForceAutoOffSection />
      </Container>
    </>
  );
};

export default AdminConfig;
